import { Collection } from "./collection";
import { bufferedPipe, Condition } from "./condition";
import { Semaphore } from "./lock";
import { Resource } from "./resource";

/**
 * Async iterable of promises
 */
export interface PromiseStream<T> extends AsyncIterable<() => Promise<T>> {}

/**
 * Return promise stream and yield results as they finish. An error halts
 * processing and is thrown. Successive errors are discarded.
 */
export async function* promiseStreamRun<T>(
  promises: PromiseStream<T>,
): AsyncIterableIterator<T> {
  const [write, read] = bufferedPipe<T>();

  let halt = false;
  const producer = (async () => {
    const pending = new Collection<Promise<void>>();
    try {
      for await (const fn of promises) {
        if (halt) {
          break;
        }
        const key = pending.add(
          fn()
            .then(
              async (value) => {
                if (halt) return;
                write.next(value);
              },
              async (e) => {
                if (halt) return;
                halt = true;
                write.throw(e);
              },
            )
            .finally(() => {
              pending.remove(key);
            }),
        );
      }
    } finally {
      await Promise.allSettled(pending);
      write.end();
    }
  })();

  try {
    yield* read;
  } finally {
    // if interrupted, stop the producer
    halt = true;
    await producer;
  }
}

/**
 * Limits concurrency of a promise stream
 */
export async function* promiseStreamThrottle<T>(
  promises: PromiseStream<T>,
  max: number,
): PromiseStream<T> {
  const semaphore = new Semaphore(max);
  await semaphore.acquire();
  for await (const fn of promises) {
    yield () => fn().finally(() => semaphore.release());
    await semaphore.acquire();
  }
}

export interface ResourcePromiseStream<R, T>
  extends AsyncIterable<(value: R) => Promise<T>> {}

export async function* resourcePromiseStreamRun<R, T>(
  resource: Resource<R>,
  stream: AsyncIterable<(value: R) => Promise<T>>,
  signal?: AbortSignal,
): AsyncIterableIterator<T> {
  const end = Symbol();
  const [write, read] = bufferedPipe<Promise<T> | typeof end>();

  let aborted = signal?.aborted ?? false;
  const onAbort = () => {
    aborted = true;
    write.end();
  };
  signal?.addEventListener("abort", onAbort);

  (async (): Promise<typeof end> => {
    try {
      if (aborted) {
        return end;
      }
      for await (const item of stream) {
        if (aborted) {
          break;
        }
        const proceed = new Condition<void>();
        write.next(
          resource(async (value) => {
            proceed.resolve();
            return await item(value);
          }).catch((e) => {
            proceed.reject(e);
            throw e;
          }),
        );
        await proceed.result;
      }
      return end;
    } finally {
      signal?.removeEventListener("abort", onAbort);
    }
  })().then(
    (v) => {
      if (!aborted) {
        write.next(v);
      } else {
        write.end();
      }
    },
    (e) => {
      if (!aborted) {
        write.throw(e);
      } else {
        write.end();
      }
    },
  );

  for await (const r of read) {
    if (r === end) {
      break;
    }
    yield r;
  }
}
